import { type TrackingTicketOptions } from '@root/lib/modules/tracking/tracking.module';

export interface TiktokPixelAdapterRegisterConfig {
	/** TikTok Pixel tracking ID */
	pixelId: string;
	/** Advanced matching parameters */
	advancedMatching?: AdvancedMatching;
}

/**
 * TikTok Pixel tracking adapter
 * @author filipditrich <ditrich@nfctron.com>
 * @export
 */
export class TikTokPixelAdapter {
	private allowTracking;
	private isInitialized: boolean = false;
	public ttqPixelIds: string[] = [];

	get ttq(): TiktokPixel | undefined {
		if (typeof window === 'undefined') return undefined;
		return (window as any)?.ttq as TiktokPixel | undefined;
	}

	constructor(allowTracking: boolean = false) {
		this.allowTracking = allowTracking;
	}

	/**
	 * Updates the tracking consent
	 * @param allowTracking Tracking consent
	 */
	public updateConsent(allowTracking: boolean) {
		this.allowTracking = allowTracking;
		if (allowTracking) this.ttq?.enableCookie();
		else this.ttq?.disableCookie();
	}

	/**
	 * Registers TikTok Pixel tracking ID
	 * @param pixelId TikTok Pixel tracking ID
	 * @param advancedMatching Advanced matching parameters
	 */
	public register({ pixelId, advancedMatching }: TiktokPixelAdapterRegisterConfig) {
		/** ignore when tracking is disallowed */
		if (!this?.allowTracking) return;
		console.debug('🧚🏻 [TikTokPixelModule] register(%s) called', pixelId);
		/** ignore re-registering the same trackingId */
		if (this.ttqPixelIds.includes(pixelId)) {
			console.debug('🧚🏻 [TikTokPixelModule] register(%s) already registered', pixelId);
			return;
		}
		this.isInitialized = typeof window !== 'undefined' && !!(window as any).ttq;
		if (!this.isInitialized) {
			console.debug('🧚🏻 [TikTokPixelModule] register(%s) initializing TikTok Pixel', pixelId);
			this.loadLibrary(window, 'ttq');
		}

		this.ttqPixelIds.push(pixelId);
		this.ttq?.load(pixelId);
		this.ttq?.identify(advancedMatching);
		this.ttq?.page();
	}

	/**
	 * Loads the TikTok Pixel library into the window object
	 * @param w Global window object
	 * @param t TikTok Pixel object name
	 */
	public loadLibrary = (w: Window, t: string) => {
		console.debug('🧚🏻 [TikTokPixelModule] loadLibrary(w, t) called');
		/* eslint-disable */
		(w as any).TiktokAnalyticsObject = t;
		var ttq = ((w as any)[t] = (w as any)[t] || []);
		ttq.methods = [
			'page',
			'track',
			'identify',
			'instances',
			'debug',
			'on',
			'off',
			'once',
			'ready',
			'alias',
			'group',
			'enableCookie',
			'disableCookie',
		];
		ttq.setAndDefer = function (t: any, e: any) {
			t[e] = function () {
				t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
			};
		};
		for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
		ttq.instance = function (t: any) {
			for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
			return e;
		};
		ttq.load = function (e: string, n: any) {
			var i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
			ttq._i = ttq._i || {};
			ttq._i[e] = [];
			ttq._i[e]._u = i;
			ttq._t = ttq._t || {};
			ttq._t[e] = +new Date();
			ttq._o = ttq._o || {};
			ttq._o[e] = n || {};
			var o = document.createElement('script');
			o.type = 'text/javascript';
			o.async = !0;
			o.src = i + '?sdkid=' + e + '&lib=' + t;
			var a = document.getElementsByTagName('script')[0];
			a?.parentNode?.insertBefore(o, a);
		};

		this.isInitialized = true;
	};

	/**
	 * Page view handler function
	 * @see https://developers.facebook.com/docs/meta-pixel/get-started
	 */
	public handlePageView = () => {
		if (!this.allowTracking || this.ttqPixelIds.length === 0) return;
		console.debug('🧚🏻 [TikTokPixelModule] handlePageView() called');
		this.ttq?.page();
	};

	/**
	 * Event handler function
	 * @param event TikTok event name
	 * @param params TikTok event parameters
	 * @param ref Additional references
	 * @see https://business-api.tiktok.com/portal/docs?id=1741601162187777
	 */
	public handleEvent = (event: TiktokEvent, params: TiktokParams, ref: EventRef) => {
		if (!this.allowTracking || this.ttqPixelIds.length === 0) return;
		console.debug('🧚🏻 [TikTokPixelModule] handleEvent(%s) called', event, params);
		this.ttq?.track(event, { ...params, ...ref });
	};

	/**
	 * Converts tickets list to TikTok Pixel ContentIds structure
	 * @param tickets List of tracking tickets
	 * @returns List of TikTok Pixel ContentIds
	 */
	public static convertTicketsToContents = (tickets: Array<TrackingTicketOptions>): Array<ContentParams> =>
		tickets.reduce((acc, ticket) => {
			/** add new ticket item */
			acc.push({
				content_id: ticket.ticketType.ticketTypeId,
				content_type: 'product',
				content_name: ticket.ticketType.namePub,
				quantity: 1,
				price: ticket.ticketType.price / 100,
			});

			/** add chip items */
			if (ticket.chips.length > 0) {
				const ticketChips = ticket.chips.map((chip, index) => ({
					content_id: chip.productItemId,
					content_type: 'product',
					content_name: 'Credit top up',
					quantity: 1,
					price: chip.amount / 100,
				}));
				acc.push(...ticketChips);
			}
			/** add extra product items */
			if (ticket.extraProducts.length > 0) {
				const ticketExtraProducts = ticket.extraProducts.reduce((accExtra, extraProduct, index) => {
					accExtra.push({
						content_id: extraProduct.ticketProductId,
						content_type: 'product',
						content_name: extraProduct.name,
						quantity: 1,
						price: extraProduct.price / 100,
					});

					/** add extra product chip items */
					if (extraProduct.chips.length > 0) {
						const extraProductChips = extraProduct.chips.map((chip, indexExtra) => ({
							content_id: chip.productItemId,
							content_type: 'product',
							content_name: 'Credit top up',
							quantity: 1,
							price: chip.amount / 100,
						}));
						acc.push(...extraProductChips);
					}

					return accExtra;
				}, [] as Array<ContentParams>);
				acc.push(...ticketExtraProducts);
			}
			return acc;
		}, [] as Array<ContentParams>);

	/**
	 * InitiateCheckout event handler
	 * @param params TikTok event parameters
	 * @param ref Additional references
	 */
	public initiateCheckout = (params: TiktokParams, ref: EventRef) => {
		this.handleEvent('InitiateCheckout', params, ref);
	};

	/**
	 * Order placement/Purchase event handler
	 * @param params TikTok event parameters
	 * @param ref Additional references
	 */
	public placeAnOrder = (params: TiktokParams, ref: EventRef) => {
		this.handleEvent('PlaceAnOrder', params, ref);
	};

	/**
	 * Complete payment/Purchase success event handler
	 * @param params TikTok event parameters
	 * @param ref Additional references
	 */
	public completePayment = (params: TiktokParams, ref: EventRef) => {
		this.handleEvent('CompletePayment', params, ref);
	};

	/**
	 * AddToCart event handler
	 * @param params TikTok event parameters
	 * @param ref Additional references
	 */
	public addToCart = (params: TiktokParams, ref: EventRef) => {
		this.handleEvent('AddToCart', params, ref);
	};
}

/**
 * TikTok Pixel parameters
 * @see https://business-api.tiktok.com/portal/docs?id=1739585702922241
 */
interface TiktokParams {
	content_type?: 'product' | 'product_group';
	content_id?: string;
	contents?: ContentParams[];
	content_category?: string;
	content_name?: string;
	value?: number;
	price?: number;
	quantity?: number;
	query?: string;
	description?: string;
	status?: string;
	currency?:
		| 'ED'
		| 'ARS'
		| 'AUD'
		| 'BDT'
		| 'BHD'
		| 'BIF'
		| 'BOB'
		| 'BRL'
		| 'CAD'
		| 'CHF'
		| 'CLP'
		| 'CNY'
		| 'COP'
		| 'CRC'
		| 'CZK'
		| 'DKK'
		| 'DZD'
		| 'EGP'
		| 'EUR'
		| 'GBP'
		| 'GTQ'
		| 'HKD'
		| 'HNL'
		| 'HUF'
		| 'IDR'
		| 'ILS'
		| 'INR'
		| 'ISK'
		| 'JPY'
		| 'KES'
		| 'KHR'
		| 'KRW'
		| 'KWD'
		| 'KZT'
		| 'MAD'
		| 'MOP'
		| 'MXN'
		| 'MYR'
		| 'NGN'
		| 'NIO'
		| 'NOK'
		| 'NZD'
		| 'OMR'
		| 'PEN'
		| 'PHP'
		| 'PKR'
		| 'PLN'
		| 'PYG'
		| 'QAR'
		| 'RON'
		| 'RUB'
		| 'SAR'
		| 'SEK'
		| 'SGD'
		| 'THB'
		| 'TRY'
		| 'TWD'
		| 'UAH'
		| 'USD'
		| 'VES'
		| 'VND'
		| 'ZAR';
}

interface ContentParams {
	content_id?: string;
	content_type?: string;
	content_name?: string;
	quantity: number;
	price: number;
}

/**
 * TikTok Pixel event names
 * @see https://business-api.tiktok.com/portal/docs?id=1739585702922241
 */
type TiktokEvent =
	| 'AddPaymentInfo'
	| 'AddToCart'
	| 'AddToWishlist'
	| 'ClickButton'
	| 'CompletePayment'
	| 'CompleteRegistration'
	| 'Contact'
	| 'Download'
	| 'InitiateCheckout'
	| 'PlaceAnOrder'
	| 'Search'
	| 'SubmitForm'
	| 'Subscribe'
	| 'ViewContent';

/**
 * TikTok Pixel Advanced Matching parameters
 * @see https://business-api.tiktok.com/portal/docs?id=1765942670613506
 */
interface AdvancedMatching {
	phone_number?: string;
	email?: string;
	external_id?: string;
}

/**
 * TikTok Pixel `ttq` object interface
 */
interface TiktokPixel {
	track: (event: TiktokEvent, params: TiktokParams, options?: DebugOptions) => void;
	load: (pixelId: string, n?: any) => void;
	identify: (advancedMatching?: AdvancedMatching) => void;
	page: () => void;
	instance: (pixelId: string) => TiktokPixel;
	enableCookie: () => void;
	disableCookie: () => void;
}

interface DebugOptions {
	debug: boolean;
}

interface EventRef extends Record<string, unknown> {}
